import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Shield, Briefcase, HardHat, Plus, RotateCw, Trash2, ArrowLeft, Users, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const ROLE_META = {
  admin: { label: "Admin", icon: Shield, cls: "bg-primary/10 text-primary" },
  innendienst: { label: "Innendienst", icon: Briefcase, cls: "bg-sky-500/10 text-sky-500" },
  sachbearbeiter: { label: "Sachbearbeiter", icon: ClipboardList, cls: "bg-amber-500/10 text-amber-600" },
  monteur: { label: "Monteur", icon: HardHat, cls: "bg-orange-500/10 text-orange-500" },
};

const emptyForm = { name: "", email: "", password: "", role: "innendienst" };

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [pwReset, setPwReset] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    try {
      const [admins, innendienst, sachbearbeiter, monteure] = await Promise.all([
        api.get("/users?role=admin").then((x) => x.data),
        api.get("/users?role=innendienst").then((x) => x.data).catch(() => []),
        api.get("/users?role=sachbearbeiter").then((x) => x.data).catch(() => []),
        api.get("/users?role=monteur").then((x) => x.data).catch(() => []),
      ]);
      setUsers([...(admins || []), ...(innendienst || []), ...(sachbearbeiter || []), ...(monteure || [])]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Laden");
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => (filter === "all" ? users : users.filter((u) => u.role === filter)),
    [users, filter]
  );

  const counts = useMemo(() => {
    const c = { all: users.length, admin: 0, innendienst: 0, sachbearbeiter: 0, monteur: 0 };
    users.forEach((u) => { c[u.role] = (c[u.role] || 0) + 1; });
    return c;
  }, [users]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        name: form.name,
        role: form.role,
      });
      toast.success(`${ROLE_META[form.role].label} angelegt`);
      setDialogOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Anlegen fehlgeschlagen");
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (!pwReset) return;
    try {
      await api.patch(`/users/${pwReset.id}/password`, { password: pwValue });
      toast.success("Passwort geändert");
      setPwReset(null);
      setPwValue("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Ändern");
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success("Konto entfernt");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Entfernen");
    }
  };

  const isSelf = (u) => u.id === user?.id;

  return (
    <div className="p-4 lg:p-8 space-y-6 animate-fade-up" data-testid="team-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <Link to="/einstellungen" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-2">
            <ArrowLeft className="h-3 w-3" /> Zurück zu Einstellungen
          </Link>
          <div className="overline">Verwaltung</div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mt-2">Team</h1>
          <p className="text-muted-foreground mt-1">Admins, Innendienst und Monteure verwalten.</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setDialogOpen(true); }} data-testid="new-team-button">
          <Plus className="h-4 w-4 mr-2" /> Neues Konto
        </Button>
      </div>

      {/* Role Filter */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2" data-testid="role-tabs">
        {[
          { k: "all", label: "Alle", icon: Users, cls: "bg-slate-100 text-slate-900 dark:bg-slate-800/60 dark:text-slate-200" },
          { k: "admin", label: "Admins", icon: Shield, cls: ROLE_META.admin.cls },
          { k: "innendienst", label: "Innendienst", icon: Briefcase, cls: ROLE_META.innendienst.cls },
          { k: "sachbearbeiter", label: "Sachbearbeiter", icon: ClipboardList, cls: ROLE_META.sachbearbeiter.cls },
          { k: "monteur", label: "Monteure", icon: HardHat, cls: ROLE_META.monteur.cls },
        ].map(({ k, label, icon: Icon, cls }) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`role-tab-${k}`}
            className={`text-left rounded-md border p-3 transition-all ${filter === k ? "border-primary shadow-sm" : "border-border hover:border-primary/40"}`}
          >
            <div className="flex items-center gap-2">
              <Icon className="h-3.5 w-3.5" />
              <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${cls}`}>{label}</span>
            </div>
            <div className="font-heading text-2xl font-bold mt-1.5 tracking-tight">{counts[k]}</div>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Card className="border-border">
          <CardContent className="p-12 text-center">
            <Users className="h-10 w-10 mx-auto text-muted-foreground" />
            <div className="font-heading text-xl font-semibold mt-4">Keine Konten</div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-list">
          {filtered.map((u) => {
            const meta = ROLE_META[u.role] || ROLE_META.monteur;
            const Icon = meta.icon;
            return (
              <Card key={u.id} className="border-border" data-testid={`team-${u.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className={`h-10 w-10 rounded-md flex items-center justify-center shrink-0 ${meta.cls}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{u.name || u.email}</span>
                        {isSelf(u) && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary text-primary-foreground">Du</span>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                      <div className={`inline-block mt-1.5 px-2 py-0.5 rounded text-[10px] font-medium ${meta.cls}`}>{meta.label}</div>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-border flex items-center gap-2">
                    {!isSelf(u) && u.role !== "admin" && (
                      <Button variant="outline" size="sm" onClick={() => { setPwReset(u); setPwValue(""); }} data-testid={`team-pw-${u.id}`} className="flex-1 h-7 text-[11px]">
                        <RotateCw className="h-3.5 w-3.5 mr-1" /> Passwort
                      </Button>
                    )}
                    {isSelf(u) && (
                      <Link to="/profil" className="flex-1">
                        <Button variant="outline" size="sm" className="w-full h-7 text-[11px]" data-testid="team-own-profile">
                          <RotateCw className="h-3.5 w-3.5 mr-1" /> Mein Profil
                        </Button>
                      </Link>
                    )}
                    {!isSelf(u) && (
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(u)} data-testid={`team-del-${u.id}`} className="h-7 text-[11px]">
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Neues Konto */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="team-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" /> Neues Konto
            </DialogTitle>
            <DialogDescription>
              Rolle bestimmt die Rechte: Admin/Innendienst = Vollzugriff, Monteur = nur zugewiesene Projekt-Phasen.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Rolle *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="team-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin — Vollzugriff</SelectItem>
                  <SelectItem value="innendienst">Innendienst — Vollzugriff</SelectItem>
                  <SelectItem value="sachbearbeiter">Sachbearbeiter — nur zugewiesene Anmeldungen</SelectItem>
                  <SelectItem value="monteur">Monteur — nur eigene Phasen</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-name">Name *</Label>
              <Input id="t-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="team-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-email">E-Mail *</Label>
              <Input id="t-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="team-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-pw">Passwort * (mind. 6 Zeichen)</Label>
              <Input id="t-pw" type="text" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="team-pw" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
              <Button type="submit" data-testid="team-submit">Anlegen</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Passwort */}
      <Dialog open={!!pwReset} onOpenChange={(v) => { if (!v) { setPwReset(null); setPwValue(""); } }}>
        <DialogContent data-testid="team-pw-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Passwort ändern</DialogTitle>
            <DialogDescription>
              Neues Passwort für <span className="font-medium text-foreground">{pwReset?.email}</span>.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={resetPassword} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Neues Passwort * (mind. 6 Zeichen)</Label>
              <Input type="text" minLength={6} value={pwValue} onChange={(e) => setPwValue(e.target.value)} required data-testid="team-pw-new" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setPwReset(null); setPwValue(""); }}>Abbrechen</Button>
              <Button type="submit" data-testid="team-pw-submit">Passwort ändern</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konto entfernen?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-foreground">{deleteTarget?.email}</span> kann sich danach nicht mehr einloggen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="team-del-confirm">Entfernen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
